import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/** Full-bleed field + vignette — depth without purple glow soup. */
export function HeroAtmosphere() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div className="absolute inset-0 bg-[#e4eaef]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_70%_40%,rgba(47,122,110,0.22),transparent_58%),radial-gradient(ellipse_90%_70%_at_12%_90%,rgba(143,111,46,0.12),transparent_55%),linear-gradient(155deg,#eef3f6_0%,#dde6ec_42%,#d5e0e6_100%)]" />

      <motion.div
        className="absolute -right-[15%] top-[-20%] h-[75vmin] w-[75vmin] rounded-full bg-essence-teal/20 blur-[100px]"
        animate={{ x: [0, 20, 0], y: [0, 12, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />

      <svg
        className="absolute inset-0 h-full w-full opacity-[0.7]"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="netStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2f7a6e" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#8f6f2e" stopOpacity="0.25" />
          </linearGradient>
        </defs>
        <g stroke="url(#netStroke)" strokeWidth="1.15" fill="none">
          <path d="M40 760 C220 620, 340 540, 520 480" />
          <path d="M180 820 C360 660, 520 560, 720 500" />
          <path d="M520 480 C700 420, 860 340, 1080 240" />
          <path d="M720 500 C900 440, 1040 360, 1240 260" />
          <path d="M640 820 C780 640, 920 560, 1120 480" />
          <path d="M960 700 C1080 560, 1200 460, 1380 340" />
          <path d="M40 420 C200 380, 360 400, 520 480" />
          <path d="M1080 240 C1180 180, 1280 140, 1400 80" />
        </g>
        <g fill="#2f7a6e">
          <circle cx="40" cy="760" r="3.5" opacity="0.5" />
          <circle cx="520" cy="480" r="5" />
          <circle className="animate-pulse-node" cx="720" cy="500" r="4.5" />
          <circle cx="1080" cy="240" r="5" />
          <circle cx="1240" cy="260" r="3.5" fill="#8f6f2e" />
          <circle cx="1120" cy="480" r="3.5" opacity="0.7" />
          <circle cx="1380" cy="340" r="4" fill="#8f6f2e" opacity="0.8" />
        </g>
      </svg>

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_42%,rgba(18,22,28,0.14)_100%)]" />
      <div className="absolute inset-y-0 left-0 w-[42%] bg-gradient-to-r from-[#e8eef2]/90 via-[#e8eef2]/45 to-transparent md:w-[28%] md:from-[#e8eef2]/85 md:via-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-[14%] bg-gradient-to-t from-[#d5e0e6]/65 to-transparent md:h-[18%] md:from-[#d5e0e6]/70" />
      <div className="essence-grain absolute inset-0 opacity-[0.05]" />
    </div>
  );
}

function useLedgerBalance(start = 128.4) {
  const reduce = useReducedMotion();
  const [value, setValue] = useState(start);

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => {
      setValue((v) => {
        const next = Math.round((v + 0.002 + Math.random() * 0.012) * 10000) / 10000;
        return next > start + 0.45 ? start : next;
      });
    }, 2600);
    return () => window.clearInterval(id);
  }, [reduce, start]);

  return value;
}

/**
 * Self OS Rewards hero phone — UI matched to the live app screenshot,
 * with Available Essence ticking. Optional photoreal plate behind the chrome.
 */
export function HeroPhone() {
  const balance = useLedgerBalance(128.4);
  const available = balance.toFixed(4);
  const lifetime = (balance + 0).toFixed(4);
  const talkCredits = Math.floor(balance * 100);

  return (
    <motion.div
      className="hero-phone relative h-full w-auto"
      initial={{ opacity: 0, x: 16, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.85, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
      aria-hidden
    >
      <div className="relative h-full aspect-[9/19]">
        {/* Soft product plate (generated from app screenshot) — depth behind chrome */}
        <img
          src="/assets/hero-self-os-rewards.png"
          alt=""
          className="pointer-events-none absolute -inset-[6%] h-[112%] w-[112%] scale-110 object-cover opacity-[0.22] blur-[18px]"
          draggable={false}
        />

        <div className="absolute inset-0 rounded-[2.55rem] bg-[#0c0c0c] shadow-[0_24px_48px_-18px_rgba(18,22,28,0.42)] ring-1 ring-white/10 md:shadow-[0_50px_100px_-28px_rgba(18,22,28,0.55)]">
          <div className="absolute inset-[7px] flex flex-col overflow-hidden rounded-[2.1rem] bg-[#111111] text-[#f2ead8]">
            {/* Status bar */}
            <div className="flex items-center justify-between px-5 pt-3 text-[10px] text-white/40">
              <span>9:41</span>
              <span className="h-1.5 w-14 rounded-full bg-black/50" />
              <span className="flex gap-1">
                <span className="h-2 w-3 rounded-sm bg-white/25" />
                <span className="h-2 w-2 rounded-full bg-white/25" />
              </span>
            </div>

            {/* Rewards scroll */}
            <div className="flex-1 overflow-hidden px-4 pb-3 pt-2">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">Rewards</p>
              <h2 className="mt-1 font-display text-[1.35rem] font-bold leading-tight text-[#f2ead8] sm:text-[1.5rem]">
                Essence rewards
              </h2>
              <p className="mt-1.5 text-[11px] leading-snug text-white/45">
                Contribute idle compute to Resonatia and earn Essence on SELF OS.
              </p>

              {/* Device status */}
              <div className="mt-4 rounded-2xl border border-white/10 px-3.5 py-3">
                <p className="text-[11px] text-white/40">Device status</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-white/80 animate-pulse-node" />
                  <span className="text-sm font-semibold text-[#f2ead8]">Offline</span>
                </div>
                <p className="mt-0.5 text-[11px] text-white/40">Compute sharing is off</p>
              </div>

              {/* Balances */}
              <div className="mt-2.5 grid grid-cols-2 gap-2">
                <div className="rounded-2xl border border-white/10 px-3 py-3">
                  <p className="text-[10px] text-white/40">Available Essence</p>
                  <p className="mt-1 text-[13px] font-semibold leading-snug text-[#f2ead8] tabular-nums sm:text-sm">
                    {available}
                    <span className="mt-0.5 block text-[10px] font-normal text-white/45">Essence</span>
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 px-3 py-3">
                  <p className="text-[10px] text-white/40">Pending Essence</p>
                  <p className="mt-1 text-[13px] font-semibold leading-snug text-[#f2ead8] tabular-nums sm:text-sm">
                    0.0000
                    <span className="mt-0.5 block text-[10px] font-normal text-white/45">Essence</span>
                  </p>
                </div>
              </div>

              {/* Convert */}
              <div className="mt-2.5 rounded-2xl border border-white/10 px-3.5 py-3">
                <p className="text-sm font-semibold text-[#f2ead8]">Convert to Talk credits</p>
                <p className="mt-1 text-[10px] leading-snug text-white/45">
                  1 Essence ≈ 100 Talk credits ($1 notional at $0.01/credit).
                </p>
                <p className="mt-2 text-[10px] text-white/40 tabular-nums">
                  Cloud ledger: {available} Essence · ≈ {talkCredits} Talk credits
                </p>
                <div className="mt-3 rounded-full bg-[#e8dcc6] py-2.5 text-center text-[12px] font-semibold text-[#1a1a1a]">
                  Convert available Essence
                </div>
              </div>

              {/* Lifetime */}
              <div className="mt-2.5 rounded-2xl border border-white/10 px-3.5 py-3">
                <p className="text-[11px] text-white/40">Lifetime Essence</p>
                <p className="mt-1 text-base font-semibold text-[#f2ead8] tabular-nums">{lifetime} Essence</p>
                <p className="mt-1 text-[10px] text-white/40">12.40 hours · 48 tasks · 9.2 compute units</p>
              </div>
            </div>

            {/* Bottom nav */}
            <div className="flex items-center justify-around border-t border-white/10 px-2 py-2.5 text-[11px] text-white/45">
              <span>Home</span>
              <span>Wallet</span>
              <span className="rounded-full border border-[#c9b896]/50 px-3 py-1 text-[#f2ead8]">Rewards</span>
            </div>
          </div>

          {/* Notch */}
          <div className="absolute left-1/2 top-[12px] h-[18px] w-[28%] -translate-x-1/2 rounded-full bg-black" />
        </div>
      </div>
    </motion.div>
  );
}
